import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { store } from '@/store';
import { clearAuth, setAuth } from '@/store/authSlice';
import { ApiResponse, AuthSessionPayload } from '@/types';
import { getBrowserHint } from '@/utils/browserHint';
import { ROUTES, STORAGE_KEYS } from '@/utils/constants';

type RetriableConfig = InternalAxiosRequestConfig & { retried?: boolean };

const baseURL = import.meta.env.VITE_API_BASE_URL;

const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  // 30s (was 10s): on a slow connection a 10s cap aborted requests client-side
  // while the server had already applied them - e.g. a lead ARN edit that saved
  // but errored on the client, so the list never invalidated and kept showing
  // the old ARN. Heavier calls (email send) still raise this per-request.
  timeout: 30_000,
  // Send the httpOnly refresh + device cookies with every request.
  withCredentials: true,
});

// A bare client (no interceptors) used only to refresh, so it can't recurse.
const refreshClient = axios.create({ baseURL, withCredentials: true });

// Single-flight: concurrent 401s share one in-flight refresh call.
let refreshPromise: Promise<string> | null = null;

const runRefresh = async (): Promise<string> => {
  const { data } = await refreshClient.post<ApiResponse<AuthSessionPayload>>('/auth/refresh');
  const payload = data.data;
  if (!payload) {
    throw new Error('Refresh returned no session');
  }
  store.dispatch(setAuth({ accessToken: payload.accessToken, user: payload.user }));
  return payload.accessToken;
};

export const refreshAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = runRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const attachBrowserHint = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const hint = getBrowserHint();
  if (hint) {
    // eslint-disable-next-line no-param-reassign
    config.headers['X-Client-Browser'] = hint;
  }
  return config;
};

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = store.getState().auth;
    if (accessToken) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return attachBrowserHint(config);
  },
  (error: AxiosError) => Promise.reject(error),
);

refreshClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => attachBrowserHint(config),
  (error: AxiosError) => Promise.reject(error),
);

/**
 * Rewrites the server's generic 422 envelope into a message that names the
 * offending fields.
 *
 * The `validate()` preHandler always answers `{ message: 'Validation failed',
 * errors: [{ field, message }] }`. Every caller shows `message` alone, so a
 * schema rejection reached the user as a bare "Validation failed" with no clue
 * which field or which request was at fault - identical text whether the ARN
 * was malformed, a product payload carried an unexpected key, or a GSTIN did
 * not match. Folding the field errors in makes the toast self-explanatory.
 */
const describeValidationError = (error: AxiosError): void => {
  const body = error.response?.data as
    { message?: string; errors?: { field?: string; message?: string }[] } | undefined;
  if (!body?.errors?.length) return;
  const parts = body.errors.map((e) =>
    e.field ? `${e.field}: ${e.message ?? 'invalid'}` : (e.message ?? 'invalid'),
  );
  body.message = `${body.message ?? 'Validation failed'} - ${parts.join('; ')}`;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 422) describeValidationError(error);
    const original = error.config as RetriableConfig | undefined;
    const isAuthCall =
      original?.url?.includes('/auth/refresh') || original?.url?.includes('/auth/verify-otp');

    if (error.response?.status === 401 && original && !original.retried && !isAuthCall) {
      original.retried = true;
      try {
        const token = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${token}`;
        return await apiClient(original);
      } catch (refreshError) {
        store.dispatch(clearAuth());
        if (window.location.pathname !== ROUTES.LOGIN) {
          // A full reload can't carry React state, so flag it for LoginPage to
          // pick up after remount and explain why the user landed back here.
          sessionStorage.setItem(STORAGE_KEYS.SESSION_EXPIRED, '1');
          window.location.href = ROUTES.LOGIN;
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
